import { formatSingaporeDateTime } from '../utils/dateTime';
import type { ApplicationSubmissionStatus, ApplyForOption, BillingSoaOption, PaymentTermsOption } from '../types/applicationForm';

export const STATUS_LABEL: Record<ApplicationSubmissionStatus, string> = {
  pending_review: 'Pending verification',
  approved: 'Approved',
  rejected: 'Rejected',
  kiv: 'KIV',
  saved: 'Submitted',
  posted: 'Posted',
  cancelled: 'Cancelled',
  new: 'New',
  processing: 'Processing',
  pending_approval: 'Pending AR approval',
};

export function statusClass(status: ApplicationSubmissionStatus): string {
  if (status === 'pending_review' || status === 'pending_approval' || status === 'processing') {
    return 'af-status af-status--pending';
  }
  if (status === 'approved' || status === 'posted' || status === 'saved' || status === 'new') {
    return 'af-status af-status--approved';
  }
  if (status === 'rejected' || status === 'cancelled') return 'af-status af-status--rejected';
  return 'af-status af-status--kiv';
}

export function formatSubmittedAt(iso: string): string {
  return formatSingaporeDateTime(iso);
}

export function displayValue(value: string | null | undefined): string {
  const trimmed = value?.trim();
  return trimmed ? trimmed : '—';
}

/** List/PDF: "Jason (Elis)" = Submitted by (Invoice Issued by). */
export function formatCffSubmittedByLabel(
  submittedBy: string | null | undefined,
  invoiceIssuedBy?: string | null | undefined,
  options?: {
    /** When true, omit submitter name (stamp replaces it); keep `(issuer)` only. */
    omitSubmitterName?: boolean;
  },
): string {
  const submitter = submittedBy?.trim() || '';
  const issuer = invoiceIssuedBy?.trim() || '';
  if (options?.omitSubmitterName) {
    // Name is replaced by the PDF seal; issuer line is drawn under the seal.
    return '';
  }
  if (submitter && issuer) return `${submitter} (${issuer})`;
  if (submitter) return submitter;
  if (issuer) return `(${issuer})`;
  return '—';
}

export const APPLY_FOR_LABEL: Record<ApplyForOption, string> = {
  new_customer: 'New Account',
  new_outlet: 'New Outlet',
};

export const BILLING_SOA_LABEL: Record<BillingSoaOption, string> = {
  individual: 'Individual',
  group: 'Group',
};

export const PAYMENT_TERMS_OPTIONS: { value: PaymentTermsOption; label: string }[] = [
  { value: 'cod', label: 'COD' },
  { value: '30_days', label: '30 Days' },
  { value: '45_days', label: '45 Days' },
  { value: '60_days', label: '60 Days' },
  { value: 'paynow', label: 'PAYNOW' },
  { value: 'cod_1', label: 'COD-1' },
  { value: 'cod_3', label: 'COD-3' },
];

export const PAYMENT_TERMS_LABEL: Record<PaymentTermsOption, string> = {
  cod: 'COD',
  '30_days': '30 Days',
  '45_days': '45 Days',
  '60_days': '60 Days',
  paynow: 'PAYNOW',
  cod_1: 'COD-1',
  cod_3: 'COD-3',
};

export function formatApplyFor(value: string | null | undefined): string {
  if (value === 'new_customer' || value === 'new_outlet') return APPLY_FOR_LABEL[value];
  return '—';
}

export function formatBillingSoa(value: string | null | undefined): string {
  if (value === 'individual' || value === 'group') return BILLING_SOA_LABEL[value];
  return '—';
}

export function formatPaymentTerms(value: string | null | undefined): string {
  if (!value) return '—';
  const key = value as PaymentTermsOption;
  return PAYMENT_TERMS_LABEL[key] ?? '—';
}

export function isValidPaymentTerms(value: string): value is PaymentTermsOption {
  return PAYMENT_TERMS_OPTIONS.some((opt) => opt.value === value);
}
